import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { Plus, Bank, Coins, ClockCounterClockwise } from "@phosphor-icons/react";

const emptyForm = () => ({
    name: "", loan_type: "settlement", lender_name: "", opened_on: new Date().toISOString().slice(0, 10),
    original_principal: "", settlement_offer: "",
    monthly_interest_amount: "",
    principal_outstanding: "",
    annual_rate_pct: "", tenure_months: "", emi_start_date: "", emi_amount: "",
    funds_used_up: false,
});

export default function LoansList() {
    const [loans, setLoans] = useState([]);
    const [summary, setSummary] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        try {
            const [l, s] = await Promise.all([api.get("/loans"), api.get("/loans/summary")]);
            setLoans(l.data);
            setSummary(s.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const body = { ...form };
        for (const k of ["original_principal","settlement_offer","monthly_interest_amount","principal_outstanding","annual_rate_pct","emi_amount"]) {
            body[k] = body[k] === "" ? null : Number(body[k]);
        }
        body.tenure_months = body.tenure_months === "" ? null : parseInt(body.tenure_months, 10);
        if (!body.name.trim()) return toast.error("Name is required");
        if (body.loan_type === "settlement" && (!body.settlement_offer || body.settlement_offer <= 0)) return toast.error("Settlement offer must be > 0");
        if ((body.loan_type === "od" || body.loan_type === "term") && (!body.principal_outstanding || body.principal_outstanding <= 0)) return toast.error("Principal outstanding must be > 0");
        setSaving(true);
        try {
            const { data } = await api.post("/loans", body);
            toast.success(`Loan added — ${data.name}`);
            setDialogOpen(false);
            setForm(emptyForm());
            load();
            if (body.loan_type === "term") nav(`/loans/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Loans"
                sub="Old settlements, OD interest-only, and term loans. Track outstanding, plan payments, book EMIs from bank statement."
                right={<Button onClick={() => { setForm(emptyForm()); setDialogOpen(true); }} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="add-loan-btn"><Plus size={16} className="mr-2" />New loan</Button>}
            />
            <div className="p-8 space-y-6">
                {summary ? (
                    <div className="grid grid-cols-4 gap-4">
                        <StatCard icon={<Bank size={18} />} label="Total outstanding" value={summary.grand_outstanding} tone="dark" testid="total-outstanding" bold />
                        <StatCard icon={<ClockCounterClockwise size={18} />} label="Settlement remaining" value={summary.settlement_remaining_total} tone="rose" testid="tot-settlement" />
                        <StatCard icon={<Coins size={18} />} label="OD principal" value={summary.od_principal_total} tone="teal" testid="tot-od" sub={`+ ₹${inr(summary.monthly_interest_total)} monthly interest`} />
                        <StatCard icon={<Bank size={18} />} label="Term loan principal" value={summary.term_principal_total} tone="teal" testid="tot-term" sub={`EMI ₹${inr(summary.monthly_emi_total)}/mo`} />
                    </div>
                ) : null}

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="loans-table">
                        <thead><tr>
                            <th>Loan</th><th>Type</th><th>Lender</th><th>Outstanding</th><th>Details</th><th>Status</th>
                        </tr></thead>
                        <tbody>
                            {loans.length === 0 ? <tr><td colSpan="6" className="py-8 text-stone-500 text-center">No loans yet. Add your first one.</td></tr> :
                                loans.map((l) => (
                                    <tr key={l.id} className="cursor-pointer" onClick={() => nav(`/loans/${l.id}`)} data-testid={`row-${l.id}`}>
                                        <td className="font-medium">{l.name}</td>
                                        <td><TypeChip t={l.loan_type} /></td>
                                        <td className="text-xs text-stone-500">{l.lender_name || "—"}</td>
                                        <td className="se-num font-medium">
                                            {l.loan_type === "settlement" ? `₹ ${inr(l.settlement_remaining)} / ₹ ${inr(l.settlement_offer)}` :
                                             `₹ ${inr(l.principal_outstanding)}`}
                                        </td>
                                        <td className="text-xs text-stone-500">
                                            {l.loan_type === "settlement" ? `Paid ₹${inr(l.settlement_paid)} of ₹${inr(l.settlement_offer)}` :
                                             l.loan_type === "od" ? `${inr(l.monthly_interest_amount)}/mo interest` :
                                             `EMI ${inr(l.emi_amount)} · ${l.amortization_status === "confirmed" ? (l.amortization_rows?.filter((r) => r.paid).length || 0) + `/${l.amortization_rows?.length || 0} paid` : "schedule pending"}`}
                                        </td>
                                        <td><StatusChip status={l.status} /></td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl" data-testid="loan-dialog">
                    <DialogHeader><DialogTitle>New loan</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2" data-testid="loan-type-toggle">
                            {[["settlement", "Old / Settlement", "#9f1239"], ["od", "OD (interest-only)", "#0f766e"], ["term", "Term (EMI)", "#0d6efd"]].map(([k, label, color]) => (
                                <button key={k} onClick={() => setForm({ ...form, loan_type: k })}
                                    className={`flex-1 px-3 py-2 rounded-sm border text-sm ${form.loan_type === k ? "text-white" : "text-stone-600"}`}
                                    style={{ background: form.loan_type === k ? color : "#fff", borderColor: form.loan_type === k ? color : "var(--se-border)" }}
                                    data-testid={`loan-type-${k}`}
                                >{label}</button>
                            ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Loan name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.loan_type === "term" ? "e.g. HDFC Machinery 2025" : "e.g. HDFC OD 2024"} data-testid="loan-name" /></div>
                            <div><Label>Lender</Label><Input value={form.lender_name} onChange={(e) => setForm({ ...form, lender_name: e.target.value })} placeholder="e.g. HDFC Bank" data-testid="loan-lender" /></div>
                            <div><Label>Opened on</Label><Input type="date" value={form.opened_on} onChange={(e) => setForm({ ...form, opened_on: e.target.value })} data-testid="loan-opened-on" /></div>
                        </div>

                        {form.loan_type === "settlement" ? (
                            <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                                <div><Label>Original principal outstanding (₹)</Label><Input type="number" value={form.original_principal} onChange={(e) => setForm({ ...form, original_principal: e.target.value })} data-testid="loan-orig-principal" /></div>
                                <div><Label>Settlement offer (₹) *</Label><Input type="number" value={form.settlement_offer} onChange={(e) => setForm({ ...form, settlement_offer: e.target.value })} data-testid="loan-settle-offer" /></div>
                                <p className="col-span-2 text-[11px] text-stone-500">Only the settlement offer is booked. Original principal is display-only — nothing hits the ledger until you pay. When fully paid, the loan is auto-closed and no phantom principal remains.</p>
                            </div>
                        ) : form.loan_type === "od" ? (
                            <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                                <div><Label>Principal outstanding (₹) *</Label><Input type="number" value={form.principal_outstanding} onChange={(e) => setForm({ ...form, principal_outstanding: e.target.value })} data-testid="loan-od-principal" /></div>
                                <div><Label>Monthly interest (₹)</Label><Input type="number" value={form.monthly_interest_amount} onChange={(e) => setForm({ ...form, monthly_interest_amount: e.target.value })} data-testid="loan-od-int" /></div>
                                <p className="col-span-2 text-[11px] text-stone-500">Opening entry: Dr Suspense · Cr Loan Payable — {form.name || "…"}. Each monthly interest payment: Dr Loan Interest Expense, Cr Bank.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                                <div><Label>Principal outstanding (₹) *</Label><Input type="number" value={form.principal_outstanding} onChange={(e) => setForm({ ...form, principal_outstanding: e.target.value })} data-testid="loan-term-principal" /></div>
                                <div><Label>EMI amount (₹)</Label><Input type="number" value={form.emi_amount} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} data-testid="loan-emi" /></div>
                                <div><Label>Annual rate (%)</Label><Input type="number" step="0.01" value={form.annual_rate_pct} onChange={(e) => setForm({ ...form, annual_rate_pct: e.target.value })} data-testid="loan-rate" /></div>
                                <div><Label>Tenure (months)</Label><Input type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} data-testid="loan-tenure" /></div>
                                <div><Label>EMI start date</Label><Input type="date" value={form.emi_start_date} onChange={(e) => setForm({ ...form, emi_start_date: e.target.value })} data-testid="loan-emi-start" /></div>
                                <p className="col-span-2 text-[11px] text-stone-500">After saving, you&apos;ll upload the bank&apos;s amortization schedule (PDF/Excel). The system will extract rows, you confirm, then every EMI splits into interest + principal.</p>
                            </div>
                        )}

                        <div className="border rounded-sm p-3 flex items-start gap-3"
                             style={{ borderColor: "var(--se-border)", background: form.funds_used_up ? "#fef2f2" : "#f8fafc" }}>
                            <input type="checkbox"
                                   id="funds-used-up"
                                   checked={!!form.funds_used_up}
                                   onChange={(e) => setForm({ ...form, funds_used_up: e.target.checked })}
                                   className="mt-1"
                                   data-testid="loan-funds-used-up" />
                            <label htmlFor="funds-used-up" className="text-sm cursor-pointer">
                                <div className="font-medium">Money already used up before onboarding</div>
                                <div className="text-[11px] text-stone-600 mt-1">
                                    Tick this if the loan proceeds were spent (salaries, expenses, cash withdrawn) <b>before</b> this system started tracking. Net Worth will drop by ₹{inr(Number(form.loan_type === "settlement" ? form.settlement_offer : form.principal_outstanding) || 0)} immediately.
                                    <br />Leave unticked if the money is still sitting in the bank — you&apos;ll reclassify Suspense to Bank later via reconciliation.
                                </div>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="save-loan">{saving ? "Saving…" : "Add loan"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function StatCard({ icon, label, value, tone = "dark", sub, testid, bold = false }) {
    const bg = tone === "rose" ? "rgba(225, 29, 72, 0.05)" : tone === "teal" ? "rgba(20, 184, 166, 0.05)" : "#fff";
    const color = tone === "rose" ? "#9f1239" : tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)", background: bg }} data-testid={testid}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">{icon}{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>₹ {inr(value || 0)}</div>
            {sub ? <div className="text-[11px] text-stone-500 mt-1">{sub}</div> : null}
        </div>
    );
}

function TypeChip({ t }) {
    const label = t === "settlement" ? "settlement" : t === "od" ? "OD" : "term";
    const cls = t === "settlement" ? "se-chip-warn" : t === "od" ? "se-chip-teal" : "se-chip-indigo";
    return <span className={`se-chip ${cls} text-[10px]`}>{label}</span>;
}
